import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/services/toast/toast.service';
import { WorkflowResponse, WorkflowService, WorkflowSummary } from 'src/app/services/workflow/workflow.service';
import { blankStage, Workflow, WorkflowTier } from '../workflows/workflow.models';

type WorkflowStatusFilter = 'All' | 'Draft' | 'Published' | 'Inactive';

@Component({
  standalone: false,
  selector: 'app-workflow',
  templateUrl: './workflow.component.html',
  styleUrls: ['./workflow.component.css'],
})
export class WorkflowComponent implements OnInit {
  constructor(
    private router: Router,
    private workflowService: WorkflowService,
    public toastService: ToastService,
  ) {}

  isCreateScreen = false;
  workflowTemplates: WorkflowSummary[] = [];
  selectedStatusFilter: WorkflowStatusFilter = 'All';
  isLoadingTemplates = false;
  templateListError = '';
  activeWorkflowTemplateMenuId: number | null = null;
  workflowTemplateMenuPosition = { top: 0, left: 0 };
  draftName = '';
  draftSku = '';
  draftReferenceCode = '0000';
  draftDescription = '';
  draftSkuTouched = false;
  selectedStageCount = 2;
  selectedTier: WorkflowTier = 'Managed';
  showNameError = false;
  nameErrorMessage = 'Template name is required';
  showDescriptionError = false;
  descriptionErrorMessage = 'Template description must not exceed 250 characters';
  isSaving = false;
  isPublishingTemplate = false;
  isPublishConfirmOpen = false;
  isDeactivatingTemplate = false;
  isDeactivateConfirmOpen = false;
  createdWorkflowTemplate?: WorkflowResponse;
  templatePendingPublish?: WorkflowSummary;
  templatePendingDeactivation?: WorkflowSummary;
  private skuSuggestionTimeout?: ReturnType<typeof setTimeout>;
  private skuSuggestionRequestId = 0;

  readonly workflowNameLimit = 100;
  readonly skuLimit = 50;
  readonly workflowDescriptionLimit = 250;
  readonly statusFilters: WorkflowStatusFilter[] = ['All', 'Draft', 'Published', 'Inactive'];
  readonly stageCounts = [1, 2, 3];
  readonly stageHints: Record<number, string> = {
    1: 'Submitter fills and sends. No approval required.',
    2: 'Submitter fills. One approver reviews and acts.',
    3: 'Submitter fills. Two sequential approvers review and act.',
  };
  readonly tiers: { value: WorkflowTier; description: string }[] = [
    {
      value: 'Managed',
      description: 'Human approver at each stage.',
    },
    {
      value: 'Automated',
      description: 'System routes without human action.',
    },
  ];

  /** Display label for a tier — the 'Managed' value is shown as "Manual". */
  tierLabel(tier: WorkflowTier | string): string {
    return tier === 'Managed' ? 'Manual' : tier;
  }

  get stageHint(): string {
    return this.stageHints[this.selectedStageCount];
  }

  ngOnInit(): void {
    this.isCreateScreen = this.router.url.includes('/settings/workflows/create-workflow');
    if (this.isCreateScreen) {
      this.resetDraft();
      return;
    }

    this.loadWorkflowTemplates();
  }

  @HostListener('document:click', ['$event'])
  closeWorkflowTemplateMenuOnOutsideClick(event: MouseEvent): void {
    if (!this.activeWorkflowTemplateMenuId) {
      return;
    }

    const target = event.target as Element | null;
    if (target?.closest('.workflow-menu-trigger, .workflow-table-menu')) {
      return;
    }

    this.closeWorkflowTemplateMenu();
  }

  closeCreateModal(): void {
    if (this.isSaving) {
      return;
    }

    this.router.navigate(['/settings/workflows']);
    this.showNameError = false;
  }

  onNameInput(value: string): void {
    this.draftName = value;
    this.showNameError = false;
    this.nameErrorMessage = 'Template name is required';

    if (!this.draftSkuTouched) {
      this.draftSku = this.createSuggestedSku(value);
    }
  }

  onDescriptionInput(value: string): void {
    this.draftDescription = value;
    this.showDescriptionError = false;
  }

  onSkuInput(value: string): void {
    this.draftSkuTouched = true;
    this.draftSku = this.normalizeSku(value);
  }

  refreshSkuSuggestion(): void {
    this.queueSkuSuggestion(0);
  }

  selectStageCount(stageCount: number): void {
    this.selectedStageCount = stageCount;
  }

  selectTier(tier: WorkflowTier): void {
    this.selectedTier = tier;
  }

  loadWorkflowTemplates(): void {
    this.isLoadingTemplates = true;
    this.templateListError = '';
    this.closeWorkflowTemplateMenu();

    this.workflowService.getAll(this.toApiStatus(this.selectedStatusFilter)).subscribe({
      next: (templates) => {
        this.workflowTemplates = templates;
        this.isLoadingTemplates = false;
      },
      error: (error) => {
        this.workflowTemplates = [];
        this.isLoadingTemplates = false;
        this.templateListError = this.getErrorMessage(error);
      },
    });
  }

  selectStatusFilter(filter: WorkflowStatusFilter): void {
    if (this.selectedStatusFilter === filter) {
      return;
    }

    this.selectedStatusFilter = filter;
    this.loadWorkflowTemplates();
  }

  trackTemplateById(_: number, template: WorkflowSummary): number {
    return template.workflowTemplateId;
  }

  formatWorkflowStatus(status: string): string {
    switch (status?.toUpperCase()) {
      case 'DRAFT':
        return 'Draft';
      case 'ACTIVE':
      case 'PUBLISHED':
        return 'Published';
      case 'INACTIVE':
      case 'ARCHIVED':
        return 'Inactive';
      default:
        return status || 'Unknown';
    }
  }

  workflowStatusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
      case 'PUBLISHED':
        return 'status-published';
      case 'INACTIVE':
      case 'ARCHIVED':
        return 'status-inactive';
      default:
        return 'status-draft';
    }
  }

  canMarkInactive(template: WorkflowSummary): boolean {
    const status = template.status?.toUpperCase();
    return status === 'ACTIVE' || status === 'PUBLISHED';
  }

  formatDate(value: string | null): string {
    if (!value) {
      return '-';
    }

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(value));
  }

  formatTime(value: string | null): string {
    if (!value) {
      return '';
    }

    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  }

  toggleWorkflowTemplateMenu(templateId: number, event: Event): void {
    event.stopPropagation();
    const trigger = event.currentTarget as HTMLElement | null;

    if (this.activeWorkflowTemplateMenuId !== templateId && trigger) {
      const triggerRect = trigger.getBoundingClientRect();
      const menuWidth = 200;
      const estimatedMenuHeight = 176;
      const viewportPadding = 16;
      const left = Math.min(
        window.innerWidth - menuWidth - viewportPadding,
        Math.max(viewportPadding, triggerRect.right - menuWidth)
      );
      const opensBelow = triggerRect.bottom + estimatedMenuHeight + viewportPadding <= window.innerHeight;

      this.workflowTemplateMenuPosition = {
        top: opensBelow
          ? triggerRect.bottom + 8
          : Math.max(viewportPadding, triggerRect.top - estimatedMenuHeight - 8),
        left,
      };
    }

    this.activeWorkflowTemplateMenuId =
      this.activeWorkflowTemplateMenuId === templateId ? null : templateId;
  }

  closeWorkflowTemplateMenu(): void {
    this.activeWorkflowTemplateMenuId = null;
  }

  continueBuilding(template: WorkflowSummary, options?: { openValidationPanel?: boolean }): void {
    this.closeWorkflowTemplateMenu();
    this.router.navigate(['/settings/workflows/build'], {
      state: {
        workflowTemplateId: template.workflowTemplateId,
        workflowMeta: {
          name: template.name,
          sku: template.sku,
          referenceCode: template.referenceCode,
          description: template.description,
          tier: template.tier as WorkflowTier,
          stageCount: template.stageCount,
        },
        openValidationPanel: options?.openValidationPanel === true,
      },
    });
  }

  openPublishedTemplate(template: WorkflowSummary): void {
    this.continueBuilding(template);
  }

  openPublishFlow(template: WorkflowSummary): void {
    this.closeWorkflowTemplateMenu();
    if (this.isPublishingTemplate) {
      return;
    }

    this.clearDeactivateConfirmation();
    this.templatePendingPublish = template;
    this.isPublishConfirmOpen = true;
  }

  cancelPublishFlow(): void {
    if (this.isPublishingTemplate) {
      return;
    }

    this.clearPublishConfirmation();
  }

  confirmPublishTemplate(): void {
    const template = this.templatePendingPublish;
    if (!template || this.isPublishingTemplate) {
      return;
    }

    this.isPublishingTemplate = true;
    this.workflowService.getById(template.workflowTemplateId).subscribe({
      next: (workflowTemplate) => {
        const workflowToPublish = {
          ...(workflowTemplate.definition || {}),
          lastValidation: workflowTemplate.lastValidation,
        } as Workflow;

        if (workflowToPublish.lastValidation?.passed !== true) {
          this.isPublishingTemplate = false;
          this.clearPublishConfirmation();
          this.continueBuilding(template, { openValidationPanel: true });
          return;
        }

        this.workflowService
          .publish(workflowTemplate.workflowTemplateId, workflowToPublish, workflowTemplate.updatedAt)
          .subscribe({
            next: () => {
              this.isPublishingTemplate = false;
              this.clearPublishConfirmation();
              this.toastService.isError = false;
              this.toastService.toastText = 'Template published successfully.';
              this.showToast();
              this.loadWorkflowTemplates();
            },
            error: (error) => {
              this.isPublishingTemplate = false;
              this.clearPublishConfirmation();
              this.showErrorToast(this.getErrorMessage(error, 'Error publishing template'));
            },
          });
      },
      error: (error) => {
        this.isPublishingTemplate = false;
        this.clearPublishConfirmation();
        this.showErrorToast(this.getErrorMessage(error, 'Error publishing template'));
      },
    });
  }

  openDeactivateFlow(template: WorkflowSummary): void {
    this.closeWorkflowTemplateMenu();
    if (!this.canMarkInactive(template)) {
      this.showErrorToast('Only published templates can be marked inactive.');
      return;
    }

    if (this.isDeactivatingTemplate) {
      return;
    }

    this.clearPublishConfirmation();
    this.templatePendingDeactivation = template;
    this.isDeactivateConfirmOpen = true;
  }

  cancelDeactivateFlow(): void {
    if (this.isDeactivatingTemplate) {
      return;
    }

    this.clearDeactivateConfirmation();
  }

  confirmDeactivateTemplate(): void {
    const template = this.templatePendingDeactivation;
    if (!template || this.isDeactivatingTemplate) {
      return;
    }

    if (!this.canMarkInactive(template)) {
      this.clearDeactivateConfirmation();
      this.showErrorToast('Only published templates can be marked inactive.');
      return;
    }

    this.isDeactivatingTemplate = true;
    this.workflowService.deactivate(template.workflowTemplateId, undefined, template.updatedAt).subscribe({
      next: () => {
        this.isDeactivatingTemplate = false;
        this.clearDeactivateConfirmation();
        this.toastService.isError = false;
        this.toastService.toastText = 'Template marked inactive.';
        this.showToast();
        this.loadWorkflowTemplates();
      },
      error: (error) => {
        this.isDeactivatingTemplate = false;
        this.clearDeactivateConfirmation();
        this.showErrorToast(this.getErrorMessage(error, 'Error marking template inactive'));
      },
    });
  }

  startBuilding(): void {
    if (this.isSaving) {
      return;
    }

    const name = this.draftName.trim();
    if (!name) {
      this.nameErrorMessage = 'Template name is required';
      this.showNameError = true;
      return;
    }

    if (name.length < 3) {
      this.nameErrorMessage = 'Template name must be at least 3 characters.';
      this.showNameError = true;
      return;
    }

    if (name.length > this.workflowNameLimit) {
      this.nameErrorMessage = `Template name must not exceed ${this.workflowNameLimit} characters`;
      this.showNameError = true;
      return;
    }

    const description = this.draftDescription.trim();
    if (description.length > this.workflowDescriptionLimit) {
      this.descriptionErrorMessage = `Template description must not exceed ${this.workflowDescriptionLimit} characters`;
      this.showDescriptionError = true;
      return;
    }

    this.draftSku = this.draftSku || this.createSuggestedSku(name) || 'WF-WORKFLOW-01';
    const workflow = this.buildInitialWorkflow(name, description);

    this.isSaving = true;
    this.workflowService.create(workflow).subscribe({
      next: (workflowTemplate) => {
        this.createdWorkflowTemplate = workflowTemplate;
        this.isSaving = false;
        this.toastService.isError = false;
        this.toastService.toastText = 'Template draft created successfully!';
        this.showToast();
        this.router.navigate(['/settings/workflows/build'], {
          state: {
            workflowTemplateId: workflowTemplate.workflowTemplateId,
            workflowMeta: {
              name: workflowTemplate.name,
              sku: workflowTemplate.sku,
              referenceCode: workflowTemplate.referenceCode,
              description: workflowTemplate.description,
              tier: workflowTemplate.tier as WorkflowTier,
              stageCount: workflowTemplate.stageCount,
            },
          },
        });
      },
      error: (error) => {
        this.isSaving = false;
        const errorMessage = this.getErrorMessage(error);
        if (
          errorMessage === 'A platform template with this name already exists' ||
          errorMessage === 'A workflow with this SKU already exists.'
        ) {
          this.nameErrorMessage = errorMessage;
          this.showNameError = true;
          return;
        }

        if (errorMessage.toLowerCase().includes('template description')) {
          this.descriptionErrorMessage = errorMessage;
          this.showDescriptionError = true;
          return;
        }

        this.showErrorToast(errorMessage);
      },
    });
  }

  private resetDraft(): void {
    if (this.skuSuggestionTimeout) {
      clearTimeout(this.skuSuggestionTimeout);
    }

    this.skuSuggestionRequestId++;
    this.draftName = '';
    this.draftSku = '';
    this.draftReferenceCode = '0000';
    this.draftDescription = '';
    this.draftSkuTouched = false;
    this.selectedStageCount = 2;
    this.selectedTier = 'Managed';
    this.showNameError = false;
    this.nameErrorMessage = 'Template name is required';
    this.showDescriptionError = false;
    this.descriptionErrorMessage = 'Template description must not exceed 250 characters';
  }

  private queueSkuSuggestion(delayMs = 300): void {
    if (this.skuSuggestionTimeout) {
      clearTimeout(this.skuSuggestionTimeout);
    }

    const workflowName = this.draftName.trim();
    if (!workflowName || workflowName.length < 3 || workflowName.length > this.workflowNameLimit) {
      this.skuSuggestionRequestId++;
      return;
    }

    const requestId = ++this.skuSuggestionRequestId;
    this.skuSuggestionTimeout = setTimeout(() => {
      const baseSku = this.draftSkuTouched
        ? this.normalizeSku(this.draftSku)
        : this.createSuggestedSku(workflowName);

      this.workflowService.getAll().subscribe({
        next: (templates) => {
          if (requestId === this.skuSuggestionRequestId && baseSku) {
            this.draftSku = this.resolveSuggestedSku(baseSku, templates);
          }
        },
        error: () => {
        },
      });
    }, delayMs);
  }

  private buildInitialWorkflow(name: string, description: string): Workflow {
    const workflow: Workflow & { description?: string; referenceCode?: string } = {
      name,
      sku: this.draftSku,
      tier: this.selectedTier,
      stageCount: this.selectedStageCount,
      activeStage: 1,
      stages: {},
      status: 'DRAFT',
      version: 1,
      finalOutputs: {
        pdf: { enabled: false, template: '' },
        email: { enabled: false, recipients: [] },
      },
      inFlightCount: 0,
      lastValidation: null,
      dirty: false,
      description,
      referenceCode: this.draftReferenceCode,
    };

    for (let index = 1; index <= this.selectedStageCount; index++) {
      workflow.stages[index] = blankStage(index);
    }

    return workflow;
  }

  private createSuggestedSku(value: string): string {
    const code = this.createSkuCodeFromName(value);
    if (!code) {
      return '';
    }

    const prefix = 'WF-';
    const suffix = '-01';
    const availableCodeLength = this.skuLimit - prefix.length - suffix.length;
    const trimmedCode = code.slice(0, availableCodeLength).replace(/-$/g, '');

    return `${prefix}${trimmedCode}${suffix}`;
  }

  private resolveSuggestedSku(baseSku: string, existingTemplates: WorkflowSummary[]): string {
    const match = baseSku.match(/^(.*-)(\d+)$/);
    if (!match) {
      return baseSku;
    }

    const prefix = match[1];
    const suffixLength = match[2].length;
    const usedSuffixes = existingTemplates
      .map((template) => template.sku)
      .filter((sku) => sku.toUpperCase().startsWith(prefix))
      .map((sku) => Number(sku.slice(prefix.length)))
      .filter((suffix) => Number.isInteger(suffix));

    const nextSuffix = usedSuffixes.length ? Math.max(...usedSuffixes) + 1 : Number(match[2]);
    return `${prefix}${String(nextSuffix).padStart(suffixLength, '0')}`;
  }

  private createSkuCodeFromName(value: string): string {
    const words = value.toUpperCase().match(/[A-Z0-9]+/g) || [];
    return words
      .map((word) => word.slice(0, 2))
      .join('')
      .slice(0, this.skuLimit);
  }

  private normalizeSku(value: string): string {
    return value
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, this.skuLimit);
  }

  private toApiStatus(filter: WorkflowStatusFilter): string | undefined {
    switch (filter) {
      case 'Draft':
        return 'DRAFT';
      case 'Published':
        return 'PUBLISHED';
      case 'Inactive':
        return 'INACTIVE';
      default:
        return undefined;
    }
  }

  private showToast(): void {
    this.toastService.showToast = true;
    setTimeout(() => {
      this.toastService.showToast = false;
    }, 5000);
  }

  private showErrorToast(message: string): void {
    this.toastService.isError = true;
    this.toastService.toastText = message;
    this.showToast();
  }

  private clearPublishConfirmation(): void {
    this.isPublishConfirmOpen = false;
    this.templatePendingPublish = undefined;
  }

  private clearDeactivateConfirmation(): void {
    this.isDeactivateConfirmOpen = false;
    this.templatePendingDeactivation = undefined;
  }

  private getErrorMessage(error: any, fallback = 'Error creating template draft'): string {
    if (typeof error?.error?.message === 'string') {
      return error.error.message;
    }

    if (typeof error?.error === 'string') {
      return error.error;
    }

    return fallback;
  }
}
